// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { formatToPar } from '@/features/statistics/components/Charts';
import { StatCard, StatRow } from '@/features/statistics/components/StatPieces';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { BAR_STRETCH, TraitBar, TraitBlock } from './TraitParts';
import type { PlayingStyle } from './types';

type Variant = keyof typeof BAR_STRETCH;

type Props = {
  playingStyle: Pick<PlayingStyle, 'sampleHoles' | 'traits' | 'nineFade' | 'parBias'> & {
    measuredToPar?: number | null;
  };
  /** `personal`: against the average golfer. `tour`: against the tour's own field. */
  variant?: Variant;
  /** Tour only: how many players the field compares. */
  fieldSize?: number;
};

/**
 * A player's personality in words and bars: the same traits a ghost of them plays with. Every
 * level comes from the service; this only picks the sentence that goes with it.
 */
export function PlayingStyleView({ playingStyle, variant = 'personal', fieldSize = 0 }: Props) {
  const { t } = useI18n();
  const { traits, nineFade, parBias } = playingStyle;
  const prefix = variant === 'tour' ? 'tourPlayingStyle' : 'playingStyle';

  const sentences = [
    ...traits
      .filter((trait) => trait.level !== 'average')
      .map((trait) => t(`${prefix}.trait.${trait.key}.${trait.level}`)),
    ...(nineFade && nineFade.level !== 'average'
      ? [t(`${prefix}.nineFade.${nineFade.level}`, { value: Math.abs(nineFade.strokesPerHole).toFixed(2) })]
      : []),
    ...parBias
      .filter((bias) => bias.level !== 'average')
      .map((bias) =>
        t(`${prefix}.parBias.${bias.level}`, { par: bias.par, value: Math.abs(bias.strokesPerHole).toFixed(2) })
      ),
  ];

  return (
    <View style={styles.container}>
      <StatCard title={t(`${prefix}.summaryTitle`)}>
        {sentences.length > 0 ? (
          sentences.map((sentence) => (
            <View key={sentence} style={styles.sentenceRow} testID="playing-style-sentence">
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.sentence}>{sentence}</Text>
            </View>
          ))
        ) : (
          <Text style={styles.sentence} testID="playing-style-sentence">
            {t(`${prefix}.allAverage`)}
          </Text>
        )}
        <Text style={styles.muted}>
          {variant === 'tour'
            ? t('tourPlayingStyle.basedOn', { holes: playingStyle.sampleHoles, field: fieldSize })
            : t('playingStyle.basedOn', { holes: playingStyle.sampleHoles })}
        </Text>
        {variant === 'personal' && playingStyle.measuredToPar != null ? (
          <Text style={styles.muted}>
            {t('playingStyle.scoringLevel', { value: formatToPar(playingStyle.measuredToPar, 2) })}
          </Text>
        ) : null}
      </StatCard>

      <StatCard title={t(`${prefix}.traitsTitle`)}>
        <Text style={styles.muted}>{t(`${prefix}.traitsHelp`)}</Text>
        {traits.map((trait) => (
          <TraitBlock key={trait.key} traitKey={trait.key} testIDPrefix="playing-style">
            <TraitBar value={trait.value} stretch={BAR_STRETCH[variant]} testID={`playing-style-bar-${trait.key}`} />
          </TraitBlock>
        ))}
      </StatCard>

      {nineFade || parBias.length > 0 ? (
        <StatCard title={t(`${prefix}.holesTitle`)}>
          <Text style={styles.muted}>{t(`${prefix}.holesHelp`)}</Text>
          {nineFade ? (
            <StatRow
              label={t('playingStyle.nineFade.label')}
              value={t('playingStyle.perHole', { value: formatToPar(nineFade.strokesPerHole, 2) })}
              testID="playing-style-nine-fade"
            />
          ) : null}
          {parBias.map((bias) => (
            <StatRow
              key={bias.par}
              label={t('playingStyle.parBias.label', { par: bias.par })}
              value={t('playingStyle.perHole', { value: formatToPar(bias.strokesPerHole, 2) })}
              testID={`playing-style-par-${bias.par}`}
            />
          ))}
        </StatCard>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  sentenceRow: {
    flexDirection: 'row',
    gap: 8,
  },
  bullet: {
    fontSize: 14,
    lineHeight: 20,
    color: appColors.accent.primary,
  },
  sentence: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: appColors.text.heading,
  },
  muted: {
    fontSize: 12.5,
    lineHeight: 18,
    color: appColors.text.muted,
  },
});
